import { Box, Button, Card, TextField, Typography } from '@mui/material';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { memo, useEffect, useState } from 'react';
import { openDialog } from 'app/store/fuse/dialogSlice';
import { useDispatch } from 'react-redux';
import CommonModal from '../Modals/Common/CommonModal';
import CropImageModal from '../Modals/CropImageModal/CropImageModal';

interface FileInput {
  id: string;
  fieldName: string;
  acceptedTypes: string[];
}

interface PreviewState {
  [key: string]: string | undefined;
}

interface AddFilesFormProps {
  fileInputs: FileInput[];
  previewLabel: string;
  previewFieldName: string;
  setAspectError?: (error: string | null) => void;
  message?: string | null;
}

const AddFilesForm = ({
  fileInputs,
  previewLabel,
  previewFieldName,
  setAspectError,
  message,
}: AddFilesFormProps) => {
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useFormContext();
  const dispatch = useDispatch();

  const watchedPreviewField = useWatch({ control, name: previewFieldName });

  const [filePreview, setFilePreview] = useState<PreviewState>({
    [previewFieldName]: watchedPreviewField,
  });
  const [imgSrc, setImgSrc] = useState<string>();
  const [ignoreAspectRatio, setIgnoreAspectRatio] = useState(false);
  const [lastFile, setLastFile] = useState<File | null>(null);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: ControllerRenderProps<FieldValues, string>,
    inputId: string,
  ) => {
    const files = (e.target as HTMLInputElement).files;

    const isCroppable = inputId === 'avatar' || inputId === 'logo';

    if (files?.[0] && isCroppable) {
      const fileUrl = URL.createObjectURL(files[0]);
      const file = files[0];
      setLastFile(file);
      const img = new Image();
      img.src = fileUrl;

      img.onload = () => {
        setFilePreview((prev) => ({ ...prev, [inputId]: fileUrl }));

        const aspectRatio = Math.ceil((img.width / img.height) * 10);
        const desiredAspectRatio = Math.ceil((16 / 9) * 10);
        setImgSrc(fileUrl);

        if (!ignoreAspectRatio && aspectRatio !== desiredAspectRatio) {
          const errorMessage = 'Logo must have an aspect ratio of 16/9';
          setAspectError?.(errorMessage);
          setValue(previewFieldName, files);
        } else {
          setAspectError?.(null);
          clearErrors(previewFieldName);
          const dt = new DataTransfer();
          dt.items.add(file);
          setValue(previewFieldName, dt.files);
          field.onChange(dt.files);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(fileUrl);
      };

      return;
    }

    if (files?.[0] && !isCroppable) {
      field.onChange(files);
      return;
    }

    setLastFile(null);
    setValue(previewFieldName, undefined);
    setFilePreview((prev) => ({ ...prev, [inputId]: undefined }));
    setAspectError?.(null);
  };

  // Re-validate or clear error when ignoreAspectRatio toggles
  useEffect(() => {
    if (!lastFile) return;
    const objectUrl = URL.createObjectURL(lastFile);
    const img = new Image();
    img.src = objectUrl;

    img.onload = () => {
      const aspectRatio = Math.ceil((img.width / img.height) * 10);
      const desiredAspectRatio = Math.ceil((16 / 9) * 10);

      if (!ignoreAspectRatio && aspectRatio !== desiredAspectRatio) {
        setAspectError?.('Logo must have an aspect ratio of 16/9');
        setValue(previewFieldName, undefined);
      } else {
        setAspectError?.(null);
        clearErrors(previewFieldName);
        const dt = new DataTransfer();
        dt.items.add(lastFile);
        setValue(previewFieldName, dt.files);
      }
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
    };
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [ignoreAspectRatio]);

  const handleUploadCroppedImage = (blob: Blob) => {
    const fileList = [] as unknown as FileList;
    const file = new File([blob], 'cropped-image.png', { type: blob.type });
    fileList[0] = file;
    setValue(previewFieldName, fileList);
    setFilePreview((prev) => ({ ...prev, [previewFieldName]: URL.createObjectURL(file) }));
    setAspectError?.(null);
  };

  const cropImageHandler = (previewLabel: string) => {
    const circularCrop = previewLabel === 'Avatar';
    dispatch(
      openDialog({
        children: (
          <CommonModal title='Crop image'>
            <CropImageModal
              imgSrc={imgSrc}
              onUploadCroppedImage={handleUploadCroppedImage}
              circularCrop={circularCrop}
            />
          </CommonModal>
        ),
      }),
    );
  };

  return (
    <Box className='flex justify-center items-center gap-36'>
      {filePreview[previewFieldName] ? (
        <Box className='flex flex-col items-center'>
          <Typography variant='h4' component='h4'>
            {previewLabel}
          </Typography>
          <Card className='mr-4 flex flex-col items-center p-10'>
            <img
              src={filePreview[previewFieldName]}
              alt={previewFieldName}
              width={200}
              style={{
                borderRadius: previewLabel === 'Avatar' ? '50%' : 'initial',
              }}
            />
          </Card>
          {previewLabel ? (
            <Box className='flex flex-col gap-8'>
              <Typography className='text-red-500'>
                {message ? message : (errors?.logo?.message as string)}
              </Typography>
              <Button
                onClick={() => cropImageHandler(previewLabel)}
                color='secondary'
                variant='contained'
              >
                Crop Image
              </Button>
              <label style={{ textAlign: 'center' }}>
                <input
                  type='checkbox'
                  checked={ignoreAspectRatio}
                  onChange={() => setIgnoreAspectRatio(!ignoreAspectRatio)}
                />{' '}
                Ignore aspect ratio
              </label>
            </Box>
          ) : null}
        </Box>
      ) : null}
      <Box className='flex flex-col items-center justify-center'>
        <Typography variant='h4' component='h4'>
          File Upload
        </Typography>

        {fileInputs.map((input) => (
          <Box className='flex flex-row items-center justify-center py-8' key={input.id}>
            <Controller
              name={input.id}
              control={control}
              render={({ field }) => {
                const err = errors[input.id]?.message as string;
                return (
                  <TextField
                    type='file'
                    label={input.fieldName}
                    size='small'
                    variant='outlined'
                    className='w-full'
                    error={!!errors[input.id]}
                    helperText={err}
                    onChange={(e) => handleFileChange(e, field, input.id)}
                    slotProps={{
                      htmlInput: {
                        accept: input.acceptedTypes.join(','),
                      },
                      inputLabel: { shrink: true },
                    }}
                  />
                );
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default memo(AddFilesForm);
